import React, { useState } from 'react';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * NewsletterSignup — opt-in form for "The AI Ledger Brief".
 *
 * Posts to POST /api/newsletter/subscribe and confirms inline. Designed
 * to drop into any page; pass `source` so we can attribute signups (e.g.
 * "homepage_footer", "coming_soon", "post_checkout"). Stays compact so
 * it sits inside narrow columns without dominating the layout.
 *
 * Why a dedicated component (vs inline JSX): we want this on the home
 * page, the coming-soon page, and eventually a post-checkout upsell.
 * Centralising the subscribe + toast + success state means one source
 * of truth for the welcome-email flow + tracking source attribution.
 */
export default function NewsletterSignup({ source = 'homepage_footer', compact = false }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${API}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), name: name.trim() || null, source }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Subscribe failed');
      setStatus('success');
      if (data.status === 'already_subscribed') {
        toast.success("You're already on the list — see you Tuesday.");
      } else if (data.status === 'resubscribed') {
        toast.success("Welcome back — we'll see you Tuesday.");
      } else {
        toast.success("You're in. Check your inbox for a welcome message.");
      }
    } catch (err) {
      setStatus('idle');
      setErrorMsg(err.message || 'Something went wrong');
      toast.error(err.message || 'Subscribe failed');
    }
  };

  if (status === 'success') {
    return (
      <div
        className="rounded-2xl border border-[#00F0FF]/30 bg-[#00F0FF]/[0.04] p-6 md:p-8 text-center"
        data-testid="newsletter-success"
        role="status"
      >
        <CheckCircle className="w-8 h-8 text-[#00F0FF] mx-auto mb-3" />
        <h3 className="text-lg font-bold text-white mb-1">You're on the list</h3>
        <p className="text-sm text-neutral-300">
          The AI Ledger Brief lands every Tuesday morning. Check your inbox for a quick hello.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border border-white/10 bg-white/[0.03] ${compact ? 'p-5' : 'p-6 md:p-8'}`}
      data-testid="newsletter-signup"
    >
      <div className="inline-flex items-center gap-1.5 mb-3 px-2.5 py-1 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10">
        <Mail className="h-3 w-3 text-[#00F0FF]" />
        <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#00F0FF]">
          The AI Ledger Brief
        </span>
      </div>
      <h3 className={`font-bold text-white tracking-tight ${compact ? 'text-xl' : 'text-2xl md:text-3xl'} mb-2`}>
        What's actually happening in AI this week.
      </h3>
      <p className="text-sm text-neutral-400 mb-5 leading-relaxed">
        3 stories that mattered + 3 hand-picked tools from the marketplace.
        Every Tuesday morning. 60-second read. No fluff, no listicles.
      </p>

      <form onSubmit={handleSubmit} className="space-y-2.5" data-testid="newsletter-form">
        {!compact && (
          <input
            type="text"
            inputMode="text"
            autoComplete="given-name"
            placeholder="First name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="newsletter-name-input"
            className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm placeholder:text-neutral-500 focus:border-[#00F0FF]/50 focus:outline-none focus:ring-1 focus:ring-[#00F0FF]/30"
          />
        )}
        <div className="flex flex-col sm:flex-row gap-2.5">
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="newsletter-email-input"
            className="flex-1 min-w-0 px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm placeholder:text-neutral-500 focus:border-[#00F0FF]/50 focus:outline-none focus:ring-1 focus:ring-[#00F0FF]/30"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            data-testid="newsletter-submit-btn"
            className="inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-xl bg-[#00F0FF] text-black font-bold text-sm hover:bg-[#00F0FF]/90 disabled:opacity-60 whitespace-nowrap"
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Joining…
              </>
            ) : (
              'Subscribe →'
            )}
          </button>
        </div>
        {errorMsg && (
          <p className="text-xs text-red-400" data-testid="newsletter-error">{errorMsg}</p>
        )}
        <p className="text-[11px] text-neutral-500 leading-snug">
          Separate from order receipts. One-click unsubscribe in every issue.
        </p>
      </form>
    </div>
  );
}
